import React from "react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import {
  PLAY_TEST_OUTCOME_VALUE_CLEAR,
  formatPlayTestBooleanValue,
  parsePlayTestEnumTypes,
} from "@seabrookstudios/pitch2table-core";
import { SmartTextField } from "../core/SmartTextField";

const ENUM_UNKNOWN = "__unknown__";

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValueInput} value
 * @returns {"yes"|"no"|"unknown"}
 */
const toBooleanSelection = (value) => {
  if (value === true) {
    return "yes";
  }
  if (value === false) {
    return "no";
  }
  return "unknown";
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValueInput} props.value
 * @param {(value: import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValueInput) => void} props.onChange
 * @param {"small"|"medium"} [props.size]
 */
export const BooleanOutcomeSelect = ({ value, onChange, size = "small" }) => {
  /**
   * @param {*} _ev
   * @param {"yes"|"no"|"unknown"|null} next
   */
  const onChangeHandler = (_ev, next) => {
    if (next === null) {
      return;
    }
    if (next === "yes") {
      onChange(true);
      return;
    }
    if (next === "no") {
      onChange(false);
      return;
    }
    onChange(PLAY_TEST_OUTCOME_VALUE_CLEAR);
  };

  return (
    <ToggleButtonGroup
      exclusive
      size={size}
      value={toBooleanSelection(value)}
      onChange={onChangeHandler}
      aria-label="Yes, no, or unknown"
    >
      <ToggleButton value="yes">Yes</ToggleButton>
      <ToggleButton value="no">No</ToggleButton>
      <ToggleButton value="unknown">Unknown</ToggleButton>
    </ToggleButtonGroup>
  );
};

/**
 * @param {object} props
 * @param {string|undefined} props.enumTypes
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValueInput} props.value
 * @param {(value: import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValueInput) => void} props.onChange
 * @param {"small"|"medium"} [props.size]
 */
export const EnumOutcomeSelect = ({ enumTypes, value, onChange, size = "small" }) => {
  const options = parsePlayTestEnumTypes(enumTypes);
  const current =
    typeof value === "string" && value !== PLAY_TEST_OUTCOME_VALUE_CLEAR ? value : "";
  const orphan =
    current &&
    !options.some((option) => option.toLocaleLowerCase() === current.toLocaleLowerCase())
      ? current
      : undefined;

  /**
   * @param {*} ev
   */
  const onChangeHandler = (ev) => {
    const next = ev.target.value;
    if (next === ENUM_UNKNOWN) {
      onChange(PLAY_TEST_OUTCOME_VALUE_CLEAR);
      return;
    }
    onChange(next);
  };

  return (
    <FormControl size={size} sx={{ minWidth: 140 }}>
      <InputLabel id="enum-outcome-label">Choice</InputLabel>
      <Select
        labelId="enum-outcome-label"
        label="Choice"
        value={current || ENUM_UNKNOWN}
        onChange={onChangeHandler}
      >
        <MenuItem value={ENUM_UNKNOWN}>Unknown</MenuItem>
        {orphan && (
          <MenuItem value={orphan}>
            {orphan}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataValueType|undefined} props.valueType
 * @param {string|undefined} [props.enumTypes]
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValueInput} props.value
 * @param {(value: import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValueInput) => void} props.onChange
 * @param {"small"|"medium"} [props.size]
 */
export const OutcomeValueField = ({ valueType, enumTypes, value, onChange, size = "small" }) => {
  if (valueType === "BOOLEAN") {
    return <BooleanOutcomeSelect value={value} onChange={onChange} size={size} />;
  }

  if (valueType === "ENUM") {
    return (
      <EnumOutcomeSelect enumTypes={enumTypes} value={value} onChange={onChange} size={size} />
    );
  }

  return (
    <SmartTextField
      size={size}
      initialValue={value === undefined || value === null ? "" : `${value}`}
      onChange={(text) => onChange(text)}
      type="number"
      textAlign="right"
    />
  );
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestOutcomeValueInput} value
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataValueType|undefined} valueType
 * @returns {string}
 */
export const formatOutcomeValueForDisplay = (value, valueType) => {
  if (valueType === "BOOLEAN") {
    return formatPlayTestBooleanValue(value === true || value === false ? value : undefined);
  }
  if (value === undefined || value === null || value === PLAY_TEST_OUTCOME_VALUE_CLEAR) {
    return "";
  }
  return `${value}`;
};
